// src/pages/AuthPage.js
// Login / Register — animated canvas background (same engine as intro)
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Reusable live-background canvas ───────────────────────────
function LiveBackground({ canvasRef }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    let raf, t = 0;

    const onResize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);

    const CYAN = '#00f5ff', PURPLE = '#7c3aed', GOLD = '#ffd700';

    // Stars
    const stars = Array.from({ length: 160 }, () => ({
      x: Math.random(), y: Math.random(), s: Math.random() * 1.4 + 0.3,
      bri: Math.random() * 6, spd: Math.random() * 0.006 + 0.002,
    }));

    // Floating code particles
    const CODE = ['</', '{}', '=>', '&&', '[]', '()', '++', 'fn', 'var', 'let', 'const'];
    const pts = Array.from({ length: 45 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      ch: CODE[Math.floor(Math.random() * CODE.length)],
      alpha: Math.random() * 0.22 + 0.06,
      size: Math.floor(Math.random() * 6 + 9),
    }));

    // Grid
    const GC = 24, GR = 16;

    // Hex rings
    const rings = [
      { r: 0, target: Math.min(W, H) * 0.46, rot: 0,    spd: 0.0006,  col: CYAN,   lw: 0.4, a: 0.10 },
      { r: 0, target: Math.min(W, H) * 0.35, rot: 0.52, spd: -0.001,  col: PURPLE, lw: 0.6, a: 0.12 },
      { r: 0, target: Math.min(W, H) * 0.24, rot: 1.04, spd: 0.0018,  col: GOLD,   lw: 0.5, a: 0.14 },
    ];

    // Orbit dots
    const orbits = [
      { r: 0, target: Math.min(W, H) * 0.42, cnt: 8, spd: 0.0007, off: 0,    sz: 2.2, col: CYAN   },
      { r: 0, target: Math.min(W, H) * 0.31, cnt: 5, spd: -0.0013, off: 0.3, sz: 1.8, col: PURPLE },
    ];

    // Scan line
    let scanY = 0;

    // Circuit corner lines
    const circuits = [
      [[0, H*0.08], [W*0.22,H*0.08], [W*0.22,H*0.22], [W*0.32,H*0.22]],
      [[W, H*0.92], [W*0.78,H*0.92], [W*0.78,H*0.78], [W*0.68,H*0.78]],
      [[0, H*0.82], [W*0.16,H*0.82], [W*0.16,H*0.65], [W*0.26,H*0.65]],
      [[W, H*0.18], [W*0.84,H*0.18], [W*0.84,H*0.35], [W*0.74,H*0.35]],
    ];

    function hexPath(cx, cy, r, sides, rot) {
      ctx.beginPath();
      for (let i = 0; i < sides; i++) {
        const a = rot + (i / sides) * Math.PI * 2;
        const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
    }

    function draw() {
      raf = requestAnimationFrame(draw);
      t++;

      // Fade in ring radii
      rings.forEach(r  => { if (r.r  < r.target)  r.r  = Math.min(r.target,  r.r  + r.target  / 80); });
      orbits.forEach(o => { if (o.r  < o.target)  o.r  = Math.min(o.target,  o.r  + o.target  / 80); });

      ctx.fillStyle = '#060810';
      ctx.fillRect(0, 0, W, H);

      // Stars
      stars.forEach(s => {
        s.bri += s.spd;
        const b = (Math.sin(s.bri) + 1) / 2;
        ctx.globalAlpha = 0.25 + b * 0.55;
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(s.x * W, s.y * H, s.s, 0, Math.PI * 2); ctx.fill();
      });

      // Grid
      ctx.globalAlpha = 0.06;
      ctx.strokeStyle = CYAN; ctx.lineWidth = 0.3;
      for (let c = 0; c <= GC; c++) {
        const x = c * (W / GC);
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let r = 0; r <= GR; r++) {
        const y = r * (H / GR);
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = CYAN;
      for (let c = 0; c <= GC; c++) for (let r = 0; r <= GR; r++) {
        ctx.beginPath(); ctx.arc(c * (W/GC), r * (H/GR), 1, 0, Math.PI * 2); ctx.fill();
      }

      // Circuit lines
      ctx.strokeStyle = CYAN; ctx.lineWidth = 0.8;
      circuits.forEach(pts => {
        ctx.globalAlpha = 0.15;
        ctx.beginPath();
        pts.forEach(([x,y], i) => i===0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y));
        ctx.stroke();
        ctx.fillStyle = CYAN; ctx.globalAlpha = 0.28;
        pts.slice(1,-1).forEach(([x,y]) => { ctx.beginPath(); ctx.arc(x,y,2,0,Math.PI*2); ctx.fill(); });
      });

      // Hex rings
      rings.forEach(h => {
        h.rot += h.spd;
        ctx.globalAlpha = h.a;
        ctx.strokeStyle = h.col; ctx.lineWidth = h.lw;
        hexPath(W/2, H/2, h.r, 6, h.rot); ctx.stroke();
      });

      // Orbit dots
      orbits.forEach(o => {
        o.off += o.spd;
        for (let i = 0; i < o.cnt; i++) {
          const a = o.off + (i / o.cnt) * Math.PI * 2;
          ctx.globalAlpha = 0.55;
          ctx.fillStyle = o.col;
          ctx.beginPath();
          ctx.arc(W/2 + Math.cos(a)*o.r, H/2 + Math.sin(a)*o.r, o.sz, 0, Math.PI*2);
          ctx.fill();
        }
      });

      // Code particles
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < -40) p.x = W+40; if (p.x > W+40) p.x = -40;
        if (p.y < -40) p.y = H+40; if (p.y > H+40) p.y = -40;
        ctx.globalAlpha = p.alpha;
        ctx.font = `${p.size}px 'Share Tech Mono',monospace`;
        ctx.fillStyle = CYAN;
        ctx.fillText(p.ch, p.x, p.y);
      });

      // Scan line
      scanY += 1.5; if (scanY > H) scanY = 0;
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = CYAN;
      ctx.fillRect(0, scanY, W, 2);

      // Subtle vignette
      const vg = ctx.createRadialGradient(W/2,H/2,H*0.25,W/2,H/2,H*0.9);
      vg.addColorStop(0, 'rgba(6,8,16,0)');
      vg.addColorStop(1, 'rgba(6,8,16,0.7)');
      ctx.globalAlpha = 1;
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
    }

    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, [canvasRef]);

  return null;
}

// ── Auth Page ──────────────────────────────────────────────────
export default function AuthPage() {
  const [mode,  setMode]  = useState('login');
  const [form,  setForm]  = useState({ username:'', email:'', password:'' });
  const [error, setError] = useState('');
  const [busy,  setBusy]  = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const canvasRef = useRef(null);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);
  useEffect(() => { setTimeout(() => setCardVisible(true), 200); }, []);

  const submit = async () => {
    setError('');
    const { email, password, username } = form;
    if (!email || !password)           { setError('Please fill all fields'); return; }
    if (password.length < 6)           { setError('Password must be 6+ characters'); return; }
    if (mode === 'register' && !username.trim()) { setError('Username is required'); return; }
    setBusy(true);
    try {
      if (mode === 'login') await login(email, password);
      else                  await register(username.trim(), email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Try again.');
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ position:'fixed', inset:0, overflow:'hidden', background:'#060810' }}>

      {/* Animated background canvas */}
      <canvas ref={canvasRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} />
      <LiveBackground canvasRef={canvasRef} />

      {/* Version tag */}
      <div style={{
        position:'absolute', bottom:16, right:20, zIndex:10,
        fontFamily:"'Orbitron',monospace", fontSize:'9px', letterSpacing:'2px',
        color:'rgba(0,245,255,0.25)', pointerEvents:'none',
      }}>CODRION v1.0 — LEARN · CODE · DOMINATE</div>

      {/* Corner decorations */}
      {[
        { top:20,  left:20,  borderTop:'1px solid rgba(0,245,255,0.35)', borderLeft:'1px solid rgba(0,245,255,0.35)' },
        { top:20,  right:20, borderTop:'1px solid rgba(0,245,255,0.35)', borderRight:'1px solid rgba(0,245,255,0.35)' },
        { bottom:20, left:20,  borderBottom:'1px solid rgba(0,245,255,0.35)', borderLeft:'1px solid rgba(0,245,255,0.35)' },
        { bottom:20, right:20, borderBottom:'1px solid rgba(0,245,255,0.35)', borderRight:'1px solid rgba(0,245,255,0.35)' },
      ].map((s, i) => (
        <div key={i} style={{ position:'absolute', width:40, height:40, ...s, zIndex:5 }} />
      ))}

      {/* Auth card */}
      <div style={{
        position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', zIndex:10,
      }}>
        <div style={{
          background:'rgba(6,8,16,0.88)',
          backdropFilter:'blur(12px)',
          border:'1px solid rgba(0,245,255,0.18)',
          borderRadius:'14px',
          padding:'44px 40px',
          width:'400px',
          boxShadow:'0 0 80px rgba(0,245,255,0.06), 0 0 160px rgba(124,58,237,0.04)',
          transform: cardVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
          opacity: cardVisible ? 1 : 0,
          transition:'all 0.55s cubic-bezier(0.22,1,0.36,1)',
        }}>

          {/* Logo */}
          <div style={{ textAlign:'center', marginBottom:'28px' }}>
            <div style={{
              fontFamily:"'Orbitron',monospace", fontSize:'32px', fontWeight:900,
              background:'linear-gradient(135deg,#00f5ff,#7c3aed)',
              WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
              letterSpacing:'5px', marginBottom:'5px',
            }}>CODRION</div>
            <div style={{
              fontFamily:"'Orbitron',monospace", fontSize:'9px', letterSpacing:'5px',
              color:'rgba(0,245,255,0.45)',
            }}>LEARN · CODE · DOMINATE</div>

            {/* Animated underline */}
            <div style={{ position:'relative', height:2, marginTop:10, overflow:'hidden' }}>
              <div style={{
                position:'absolute', left:0, top:0, height:'100%', width:'100%',
                background:'linear-gradient(90deg,transparent,#00f5ff,#7c3aed,transparent)',
                animation:'slideUnderline 2.5s ease-in-out infinite',
              }} />
            </div>
          </div>

          <style>{`
            @keyframes slideUnderline{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}
            @keyframes inputGlow{0%,100%{box-shadow:0 0 0 0 rgba(0,245,255,0.25)}50%{box-shadow:0 0 12px 2px rgba(0,245,255,0.15)}}
            .auth-inp:focus{border-color:rgba(0,245,255,0.5)!important;box-shadow:0 0 14px rgba(0,245,255,0.12)!important;outline:none}
          `}</style>

          {/* Mode tabs */}
          <div style={{ display:'flex', borderBottom:'1px solid rgba(0,245,255,0.1)', marginBottom:'24px' }}>
            {['login','register'].map(m => (
              <div key={m} onClick={() => { setMode(m); setError(''); }} style={{
                flex:1, padding:'9px', textAlign:'center', cursor:'pointer',
                fontFamily:"'Orbitron',monospace", fontSize:'10px', fontWeight:700, letterSpacing:'2px',
                color: mode===m ? '#00f5ff' : 'rgba(255,255,255,0.28)',
                borderBottom:`2px solid ${mode===m ? '#00f5ff' : 'transparent'}`,
                marginBottom:'-1px', transition:'all 0.2s',
              }}>{m.toUpperCase()}</div>
            ))}
          </div>

          {/* Form fields */}
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            {mode === 'register' && (
              <div>
                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'8px', color:'rgba(0,245,255,0.5)', letterSpacing:'2px', marginBottom:'5px' }}>USERNAME</div>
                <input className="auth-inp" placeholder="Choose your callsign" maxLength={20}
                  value={form.username} onChange={e=>set('username',e.target.value)}
                  style={inputStyle} />
              </div>
            )}
            <div>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'8px', color:'rgba(0,245,255,0.5)', letterSpacing:'2px', marginBottom:'5px' }}>EMAIL</div>
              <input className="auth-inp" type="email" placeholder="commander@codrion.io"
                value={form.email} onChange={e=>set('email',e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&submit()}
                style={inputStyle} />
            </div>
            <div>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:'8px', color:'rgba(0,245,255,0.5)', letterSpacing:'2px', marginBottom:'5px' }}>PASSWORD</div>
              <input className="auth-inp" type="password" placeholder="6+ characters"
                value={form.password} onChange={e=>set('password',e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&submit()}
                style={inputStyle} />
            </div>

            {error && (
              <div style={{
                padding:'9px 12px', background:'rgba(255,68,68,0.08)', border:'1px solid rgba(255,68,68,0.25)',
                borderRadius:'6px', color:'#ff6464', fontSize:'12px', textAlign:'center',
              }}>{error}</div>
            )}

            {/* Submit button */}
            <button onClick={submit} disabled={busy} style={{
              marginTop:'6px', padding:'14px',
              background: busy
                ? 'rgba(0,245,255,0.04)'
                : 'linear-gradient(135deg,rgba(0,245,255,0.14),rgba(124,58,237,0.14))',
              border:'1px solid rgba(0,245,255,0.28)',
              borderRadius:'7px',
              color:'#00f5ff', cursor: busy ? 'wait':'pointer',
              fontFamily:"'Orbitron',monospace", fontSize:'11px', fontWeight:700, letterSpacing:'2.5px',
              transition:'all 0.25s', position:'relative', overflow:'hidden',
            }}>
              {busy ? 'AUTHENTICATING...' : mode==='login' ? 'ENTER THE ARENA' : 'CREATE ACCOUNT'}
              {/* Shimmer effect */}
              {!busy && (
                <div style={{
                  position:'absolute', inset:0,
                  background:'linear-gradient(90deg,transparent,rgba(0,245,255,0.08),transparent)',
                  animation:'slideUnderline 2s ease-in-out infinite',
                }} />
              )}
            </button>
          </div>

          {/* Footer hint */}
          <div style={{ marginTop:'20px', textAlign:'center', fontFamily:"'Orbitron',monospace", fontSize:'8px', color:'rgba(255,255,255,0.15)', letterSpacing:'1px' }}>
            SECURE · ENCRYPTED · READY TO PLAY
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width:'100%', boxSizing:'border-box',
  background:'rgba(0,245,255,0.04)',
  border:'1px solid rgba(0,245,255,0.14)',
  borderRadius:'6px',
  padding:'11px 14px',
  color:'#e2e8f0',
  fontFamily:"'Rajdhani',sans-serif",
  fontSize:'15px',
  transition:'border-color 0.2s, box-shadow 0.2s',
};
